import { GameDocumentContext } from '../../../contexts/game-document';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Toolbar from '../toolbar';
import {
  CopyTaskAsync,
  DeleteTaskAsync,
  GetResourceById,
  GetResourceEntity,
  GetResourceKeys,
  GetResourceValue,
  GetTaskContentById,
  GetTimerById,
  SaveEntityEditorAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import { Button } from '@progress/kendo-react-buttons';
import { process, State } from '@progress/kendo-data-query';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords,
  GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import { LinkCell } from '../../../components/grid';
import { ResourceEntity, TaskEntity } from '../../../types/game-document/';
import { DeleteTaskAsync as DeleteTaskAsyncUtil } from '../../../utils/game-document/assets/tasks';
import { TaskEditorWindow } from '../../../features/game-document/tasks';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import EditDeleteCloneCell from '../../../components/grid/edit-delete-clone-cell';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { NoRecords } from '../../../components/grid/no-records';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import { uuid } from '../../../types/common-helper';
import { useSessionStorage, useToggle } from 'usehooks-ts';
import { useGameDocumentResources } from '../../../hooks/use-game-document-resources';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { downloadJSON, handleFileUpload } from '../../../utils/files';
import { getCurrentTimeStamp } from '../../../utils/date';
import { useToast } from '../../../contexts/toast-context';
import cloneDeep from 'lodash.clonedeep';

export interface ExportedTaskItem {
  taskName: string;
  description: string;
  title: string;
  titleVisibility: boolean;
}

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

interface TaskGridEntity extends TaskEntity {
  selected: boolean;
  taskContent: string;
  timer: string;
  icon: string;
  completeIcon: string;
}

const SELECTED_FIELD = 'selected';

const Tasks = () => {
  const navigate = useNavigate();
  const [state, setState] = useContext(GameDocumentContext);
  const gameDocumentFiles = useGameDocumentResources();
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-tasks',
    initialDataState
  );
  const [entityEditorValue, setEntityEditorValue] =
    useState<EntityEditor<TaskEntity>>();
  const [resourceEditorValue, setResourceEditorValue] =
    useState<EntityEditor<ResourceEntity>[]>();
  const [entityEditorIsVisible, setEntityEditorIsVisible] = useToggle(false);
  type DeleteContext =
    | { mode: 'single'; id: string }
    | { mode: 'bulk'; ids: string[] };

  const [showConfirmationDelete, setShowConfirmationDelete] =
    useState<DeleteContext | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [filteredTasks, setFilteredTasks] = useState<TaskGridEntity[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const { onToastOpen } = useToast();

  const titleGuid = uuid();
  const defaultTaskAvailableIconResId =
    state.gameDocument?.settings.designer?.defaultTaskAvailableIconResId || '';
  const defaultTaskCompletedIconResId =
    state.gameDocument?.settings.designer?.defaultTaskCompletedIconResId || '';
  const entity: TaskEntity = {
    id: '',
    name: '',
    description: '',
    titleResId: titleGuid,
    imageResId: defaultTaskAvailableIconResId,
    completeImageResId: defaultTaskCompletedIconResId,
    hideWhenComplete:
      state.gameDocument && !state.gameDocument.settings.designer
        ? false
        : !Boolean(
            state.gameDocument?.settings.designer?.taskCompletionVisibility
          )
  };

  const titleResource: ResourceEntity = {
    id: titleGuid,
    name: 'Title',
    description: '',
    type: 'text',
    value: ''
  };

  const LinkedNameCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props} />
  );

  const DeleteCell = (props: GridCellProps) => (
    <EditDeleteCloneCell
      onEditClick={() => onEditEntity(props.dataItem['id'])}
      onCloneClick={() => onCloneEntity(props.dataItem['id'])}
      onDeleteClick={() => {
        setShowConfirmationDelete({ mode: 'single', id: props.dataItem['id'] });
        setConfirmationMessage('Are you sure you want to remove the task?');
      }}
      {...props}
    />
  );

  const handleEntityEditorSubmit = useCallback(
    (
      editorEntity: EntityEditor<TaskEntity>,
      resourceEntity: EntityEditor<ResourceEntity>[]
    ) => {
      SaveEntityEditorAsync(state?.gameDocument!, editorEntity, resourceEntity)
        .then((response) => {
          if (response) {
            setEntityEditorIsVisible();
            setState((prev) => UpdateGameDocState(prev, response));
          }
        })
        .finally(() => {
          navigate(
            `/designer/${state.gameId}/assets/tasks/${editorEntity.entity.id}`
          );
        });
    },
    [setState, state.gameDocument, navigate, state.gameId]
  );

  const onAddEntity = () => {
    setEntityEditorValue({
      isNew: true,
      entity: entity
    });
    // Create resource for task (titleResId)
    setResourceEditorValue([
      {
        isNew: true,
        entity: titleResource
      }
    ]);
    setEntityEditorIsVisible();
  };

  const onEditEntity = (entityId: string) => {
    const editedEntity = state.gameDocument?.assets.tasks?.find(
      (i) => i.id === entityId
    )!;

    setEntityEditorValue({
      isNew: false,
      entity: editedEntity
    });

    let resources: EntityEditor<ResourceEntity>[] = [];

    for (const res of GetResourceKeys(editedEntity)) {
      if (typeof editedEntity[res] === 'string') {
        resources.push({
          isNew: false,
          entity: GetResourceEntity(
            state.gameDocument!,
            editedEntity[res] as string
          )
        });
      }
    }

    setResourceEditorValue(resources);

    setEntityEditorIsVisible();
  };

  const onCloneEntity = (entityId: string) => {
    CopyTaskAsync(state.gameDocument!, entityId, uuid(), true).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      }
    );
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteTaskAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        //delete annotation that assosiate with this deleted task
        const routes = cloneDeep(updatedGameDocument.assets.routes ?? []);
        const filtered = routes.filter(
          (r) => !(r.fromTaskId === entityId || r.toTaskId === entityId)
        );
        updatedGameDocument.assets.routes = filtered;

        setState((state) => UpdateGameDocState(state, updatedGameDocument));
        setShowConfirmationDelete(null);
      }
    );
  };

  const onDeleteEntities = async (entityIds: string[]) => {
    if (entityIds?.length) {
      let currentDoc = state.gameDocument!;
      const idsToDelete = new Set(entityIds);

      for (const id of entityIds) {
        currentDoc = await DeleteTaskAsyncUtil(currentDoc, id);
      }

      // remove routes associated with any of the deleted tasks
      const routes = cloneDeep(currentDoc.assets.routes ?? []);
      currentDoc.assets.routes = routes.filter(
        (r) => !idsToDelete.has(r.fromTaskId) && !idsToDelete.has(r.toTaskId)
      );

      setState((state) => UpdateGameDocState(state, currentDoc));
      setShowConfirmationDelete(null);
      setSelectedTaskIds([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTaskIds.length === 0) {
      return;
    }

    setShowConfirmationDelete({ mode: 'bulk', ids: selectedTaskIds });
    setConfirmationMessage(
      `Are you sure you want to remove these ${selectedTaskIds.length} tasks?`
    );
  };

  const handleJsonExport = async () => {
    if (selectedTaskIds.length === 0) {
      onToastOpen({
        title: 'Error',
        body: 'Please select the tasks you want to export first.',
        style: 'error'
      });

      return;
    }

    const selectedTasks = state.gameDocument?.assets.tasks?.filter((task) =>
      selectedTaskIds.includes(task.id)
    );

    const exportedTasks: ExportedTaskItem[] =
      selectedTasks?.map((task) => ({
        taskName: task.name,
        description: task.description,
        title:
          GetResourceById(state.gameDocument!, task.titleResId ?? '')?.value ??
          '',
        titleVisibility: task.isTitleVisible ?? true
      })) ?? [];

    const formattedTimestamp = getCurrentTimeStamp();
    downloadJSON(exportedTasks, `tasks-${formattedTimestamp}.json`);
  };

  const handleJsonImport = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFileUpload<ExportedTaskItem[]>(
      event,
      async (importedTasks) => {
        // Validate: must be an array
        if (!Array.isArray(importedTasks)) {
          onToastOpen({
            title: 'Error',
            body: 'Invalid JSON format. Expected an array of tasks.',
            style: 'error'
          });

          return;
        }

        // Validate: must not be empty
        if (importedTasks.length === 0) {
          onToastOpen({
            title: 'Error',
            body: 'The JSON file contains an empty array. No tasks to import.',
            style: 'error'
          });

          return;
        }

        // Validate all items before creating anything
        const invalidItems: number[] = [];
        for (let i = 0; i < importedTasks.length; i++) {
          const item = importedTasks[i];
          if (
            !item.title ||
            typeof item.title !== 'string' ||
            item.title.trim() === ''
          ) {
            invalidItems.push(i + 1);
          }
        }

        if (invalidItems.length > 0) {
          onToastOpen({
            title: 'Error',
            body: `Import failed. The following items are missing a required "title" field: ${invalidItems.join(', ')}. No tasks were created.`,
            style: 'error'
          });

          return;
        }

        // All items valid — proceed to create tasks
        let currentDoc = state.gameDocument!;
        const defaultTaskAvailableIconResId =
          currentDoc?.settings?.designer?.defaultTaskAvailableIconResId || '';
        const defaultTaskCompletedIconResId =
          currentDoc?.settings?.designer?.defaultTaskCompletedIconResId || '';

        for (const importedTask of importedTasks) {
          const newTitleGuid = uuid();

          const newTask: TaskEntity = {
            id: uuid(),
            name: importedTask.taskName.trim(),
            description: importedTask.description ?? '',
            titleResId: newTitleGuid,
            imageResId: defaultTaskAvailableIconResId,
            completeImageResId: defaultTaskCompletedIconResId,
            isTitleVisible: importedTask.titleVisibility ?? true,
            hideWhenComplete:
              currentDoc && !currentDoc.settings.designer
                ? false
                : !Boolean(
                    currentDoc?.settings.designer?.taskCompletionVisibility
                  )
          };

          const editorEntity: EntityEditor<TaskEntity> = {
            isNew: true,
            entity: newTask
          };

          const titleResource: ResourceEntity = {
            id: newTitleGuid,
            name: 'Title',
            description: '',
            type: 'text',
            value: importedTask.title ?? ''
          };

          const resourceEntities: EntityEditor<ResourceEntity>[] = [
            { isNew: true, entity: titleResource }
          ];

          const updatedDoc = await SaveEntityEditorAsync(
            currentDoc,
            editorEntity,
            resourceEntities
          );

          if (updatedDoc) {
            currentDoc = updatedDoc;
          }
        }

        setState((prev) => UpdateGameDocState(prev, currentDoc));

        onToastOpen({
          title: 'Info',
          body: `Import Success. ${importedTasks.length} task(s) created.`,
          style: 'info'
        });
      },
      (message) => {
        onToastOpen({
          title: 'Error',
          body: message,
          style: 'error'
        });
      }
    );
  };

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem) return;
    const selectedTaskId = event.dataItem['id'];

    const updatedTasks = filteredTasks?.map((item) =>
      item.id === selectedTaskId ? { ...item, selected: !item.selected } : item
    );
    setFilteredTasks(updatedTasks);

    const task = updatedTasks?.find((item) => item.id === selectedTaskId);

    if (task) {
      const updatedSelectedTaskIds = task.selected
        ? [...selectedTaskIds, selectedTaskId]
        : selectedTaskIds.filter((id) => id !== selectedTaskId);

      setSelectedTaskIds(updatedSelectedTaskIds);
    }
  };

  const onHeaderSelectionChange = () => {
    // Get only the items currently visible in the grid (after filter/sort/page)
    const visibleItems = process(filteredTasks ?? [], dataState).data;
    const visibleIds = new Set(
      visibleItems.map((item: TaskGridEntity) => item.id)
    );

    // If all visible items are selected, deselect them; otherwise select all
    const allVisibleSelected = visibleItems.every(
      (item: TaskGridEntity) => item.selected
    );

    const updatedTasks = filteredTasks?.map((item) => ({
      ...item,
      selected: visibleIds.has(item.id) ? !allVisibleSelected : item.selected
    }));

    const updatedSelectedTaskIds = updatedTasks?.reduce<string[]>(
      (ids, item) => {
        if (item.selected) {
          return [...ids, item.id];
        } else {
          return ids.filter((id) => id !== item.id);
        }
      },
      []
    );

    setSelectedTaskIds(updatedSelectedTaskIds || []);
    setFilteredTasks(updatedTasks);
  };

  useEffect(() => {
    if (!state.gameDocument?.assets.tasks) {
      setFilteredTasks([]);
      return;
    }

    const tasks = state.gameDocument?.assets.tasks.map((task) => {
      const taskContent = GetTaskContentById(
        state.gameDocument,
        task.taskContentId ?? ''
      );
      const timer = GetTimerById(state.gameDocument, task.timerAssId ?? '');

      return {
        ...task,
        title: GetResourceValue(state.gameDocument!, task.titleResId ?? ''),
        taskContent: taskContent
          ? GetResourceValue(state.gameDocument!, taskContent?.titleResId ?? '')
          : '-',
        timer: timer
          ? GetResourceValue(state.gameDocument!, timer?.titleResId ?? '')
          : '-',
        icon: 'Yes',
        completeIcon: task.hideWhenComplete ? 'No' : 'Yes',
        selected: false
      };
    }) as TaskGridEntity[];

    setFilteredTasks(tasks);
    setSelectedTaskIds([]);
  }, [state.gameDocument?.assets.tasks]);

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  useEffect(() => {
    // Set the page task.
    document.title = `Tasks - ${state.gameDocument?.name}`;
  }, [state]);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/tasks/*', text: 'Tasks' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Tasks'}
        buttonHelpSupport={{
          title: 'Task action & events guide',
          url: 'https://forum.catalystglobal.com/t/4529'
        }}>
        <Button onClick={onAddEntity} themeColor={'primary'}>
          Add task
        </Button>
        {entityEditorIsVisible && (
          <TaskEditorWindow
            toggleDialog={setEntityEditorIsVisible}
            onSubmit={handleEntityEditorSubmit}
            onClose={setEntityEditorIsVisible}
            editorEntity={entityEditorValue!}
            editorResource={resourceEditorValue!}
            editorMode={'basic'}
          />
        )}
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search tasks'}
          columnsToSearch={['name', 'description']}
          showCardMode={false}
          exportRef={gridExportRef}
          showBulkDelete={true}
          handleBulkDelete={handleBulkDelete}
          showJsonExport={true}
          handleJsonExport={handleJsonExport}
          showJsonImport={true}
          handleJsonImport={handleJsonImport}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
        />
        <ExcelExport
          data={filteredTasks ?? []}
          ref={gridExportRef}
          fileName={`${state.gameDocument?.name} tasks.xlsx`}>
          <ExcelExportColumn field="title" title="Title" />
          <ExcelExportColumn field="description" title="Description" />
          <ExcelExportColumn field="taskContent" title="Task Content" />
          <ExcelExportColumn field="timer" title="Timer" />
          <ExcelExportColumn field="icon" title="Icon" />
          <ExcelExportColumn field="completeIcon" title="Complete Icon" />
        </ExcelExport>
        <Grid
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          className={'cg-grid3'}
          dataItemKey={'id'}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: 'multiple'
          }}
          select={filteredTasks.reduce(
            (acc, item) => ({ ...acc, [item.id]: item.selected }),
            {} as { [id: string]: boolean }
          )}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
          data={process(filteredTasks ?? [], dataState)}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            filterable={false}
            field={SELECTED_FIELD}
            width={50}
            columnType="checkbox"
            headerSelectionValue={
              filteredTasks.length > 0 &&
              filteredTasks.every((item) => item.selected)
            }
          />
          <Column
            field={'title'}
            title={'Title'}
            cells={{ data: LinkedNameCell }}
          />
          <Column field={'description'} title={'Description'} />
          <Column title={'Task Content'} field={'taskContent'} />
          <Column title={'Timer'} field={'timer'} />
          <Column title={'Icon'} width={50} field={'icon'} />
          <Column width={130} title={'Complete Icon'} field={'completeIcon'} />
          <Column cells={{ data: DeleteCell }} />
          <GridNoRecords>
            <NoRecords />
          </GridNoRecords>
        </Grid>
      </div>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) => {
            if (action === 'yes') {
              if (showConfirmationDelete.mode === 'single') {
                onDeleteEntity(showConfirmationDelete.id);
              } else if (showConfirmationDelete.mode === 'bulk') {
                onDeleteEntities(showConfirmationDelete.ids);
              }
            }
            setShowConfirmationDelete(null);
          }}
          onClose={() => setShowConfirmationDelete(null)}>
          {confirmationMessage}
        </YesNoDialog>
      )}
    </>
  );
};

export default Tasks;
